import { Product } from "@omni/types/api/product/bff/product.model";
import { DeliveryType } from "@omni/types/api/product/bff/shared/enums/deliveryType.enum";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { CloudStore } from "@omni/types/api/store/cloud/store.model";
import { Axios } from "axios";
import { Action } from "redux";
import { ThunkDispatch } from "redux-thunk";

import {
    CartItemModel,
    CartModel
} from "@/redux/modules/api/data/cart/@types/cart.model";
import { CartItemConfigUpdateOrigin } from "@/redux/modules/api/data/cart/@types/cart.types";
import * as CartModule from "@/redux/modules/api/data/cart/cart.module";
import { APIStatus } from "@/redux/modules/api/data/cart/cart.module";
import { clear as clearApiMessages } from "@/redux/modules/value/apiMessages/apiMessages.module";
import {
    broadcastMessage,
    clearMessages
} from "@/redux/modules/value/messages/messages.utils";
import { TProductDetailState } from "@/redux/modules/value/productDetail/productDetail.types";
import { Translate } from "@/redux/modules/value/translations/translations.provider";
import { IState, TApiAction } from "@/redux/store.types";
import { shouldDisableAddToCart } from "@/utils/disableAddToCart";
import { ScrollOffsets, StorePickerModalStates } from "@/utils/enums";
import { ProductConfiguration } from "@/utils/productConfiguration/productConfiguration.types";

import { broadcastAddToCartNotAvailableForServiceProduct } from "../eCommerce.helpers";

export type OnSubmitHandler = (
    dispatch: ThunkDispatch<IState, Axios, Action<any>>,
    t: Translate
) => (
    productDetail: TProductDetailState,
    cart: CartModel | null | undefined,
    cartProduct: CartItemModel | undefined,
    product: Product<ProductFieldSet.Full>,
    preferredStore: CloudStore | null | undefined,
    productConfiguration: ProductConfiguration<any> | undefined,
    configuration: unknown | undefined,
    isSoldOut: boolean,
    isAdditionalService: boolean,
    openModal: () => void,
    setStorePickerModalState: React.Dispatch<
        React.SetStateAction<StorePickerModalStates>
    >
) => void;

const getSubmitDispatch = (
    dispatch: ThunkDispatch<IState, Axios, Action<any>>,
    cart: CartModel | null | undefined,
    cartProduct: CartItemModel | undefined,
    product: Product<ProductFieldSet.Full>,
    store: string | null,
    productDetail: TProductDetailState,
    productConfiguration: ProductConfiguration<any> | undefined,
    configuration: unknown | undefined
): Promise<TApiAction<any>> => {
    if (productConfiguration?.withOrigin && cartProduct) {
        return dispatch(
            CartModule.updateCartItemConfig(
                cartProduct.id!,
                productDetail.quantity,
                productDetail.deliveryType,
                productConfiguration.dataPaths(configuration),
                cartProduct,
                productConfiguration.productSubType,
                CartItemConfigUpdateOrigin.PRODUCT_PAGE,
                store || cart?.storeId
            )
        );
    }

    return dispatch(
        CartModule.addECommerceProductToCart(
            {
                deliveryType: productDetail.deliveryType,
                quantity: productDetail.quantity,
                shopId: store,
                sku: productDetail.sku,
                additionalServices: productDetail.serviceProductSkus
            },
            product,
            true,
            productConfiguration?.productSubType,
            productConfiguration?.dataPaths(configuration)
        )
    );
};

const onSubmit = (
    dispatch: ThunkDispatch<IState, Axios, Action<any>>,
    t: Translate,
    cart: CartModel | null | undefined,
    productDetail: TProductDetailState,
    cartProduct: CartItemModel | undefined,
    product: Product<ProductFieldSet.Full>,
    productConfiguration: ProductConfiguration<any> | undefined,
    configuration: unknown | undefined,
    deliveryType: DeliveryType,
    store: string | null,
    isAdditionalService: boolean,
    openModal: () => void
) => {
    if (isAdditionalService) {
        broadcastAddToCartNotAvailableForServiceProduct(t);
        return;
    }

    const previousStore = cart?.storeId;

    getSubmitDispatch(
        dispatch,
        cart,
        cartProduct,
        product,
        store,
        productDetail,
        productConfiguration,
        configuration
    ).then((action) => {
        if (action.type === APIStatus.SUCCESS) {
            openModal();
            determineWarningMessage(action.payload);
        } else if (action.type === APIStatus.FAILURE) {
            broadcastMessage({
                id: "pdp-error",
                text: action.payload.message,
                status: "danger",
                dismissible: true
            });
        }
    });

    const hasSameCncSkuInCartWithMinimumQuantity = (
        newCartObject: CartModel
    ) => {
        const sameSkuInCart = cart?.items?.find(
            (item) => item.sku === productDetail.sku
        );

        if (
            sameSkuInCart &&
            sameSkuInCart.deliveryType === DeliveryType.ClickAndCollect
        ) {
            const newCartItem = newCartObject.items?.find(
                (item) => item.sku === productDetail.sku
            );
            return (
                newCartItem?.minimumOrderQuantity &&
                newCartItem.minimumOrderQuantity > 1 &&
                newCartItem.roundingValue &&
                newCartItem.quantity % newCartItem.roundingValue === 0
            );
        }

        return false;
    };

    const determineWarningMessage = (newCartObject: CartModel) => {
        let warningMessage = "";

        if (
            !cart?.items.some(
                (i) => i.deliveryType === DeliveryType.ClickAndCollect
            )
        ) {
            return;
        }

        if (
            previousStore &&
            previousStore !== store &&
            deliveryType === DeliveryType.ClickAndCollect
        ) {
            warningMessage = t(
                "product.addtocart.modal.warning.differentStore"
            );
        } else if (
            deliveryType === DeliveryType.HomeDelivery &&
            hasSameCncSkuInCartWithMinimumQuantity(newCartObject)
        ) {
            warningMessage = t(
                "product.addtocart.modal.warning.quantityChanged"
            );
        }

        if (warningMessage) {
            broadcastMessage({
                id: "add-to-cart-modal-messages",
                text: warningMessage,
                status: "warning",
                dismissible: true
            });
        }
    };
};

const onSubmitHandler: OnSubmitHandler =
    (dispatch, t) =>
    (
        productDetail,
        cart,
        cartProduct,
        product,
        preferredStore,
        productConfiguration,
        configuration,
        isSoldOut,
        isAdditionalService,
        openModal,
        setStorePickerModalState
    ) => {
        if (isSoldOut) return;

        clearMessages();
        dispatch(clearApiMessages);

        const isParentInCart = (product.accessoryParents || []).some((aP) =>
            cart?.items.some((i) => i.sku === aP.sku)
        );

        if (!product.saleableSeparately && (!isParentInCart || !cart)) {
            broadcastMessage({
                id: "pdp-error",
                text: t("pdp.error.notSaleableSeparately"),
                status: "danger",
                dismissible: true,
                postFix: {
                    onClick: () => {
                        // is een dichtgeklapt info block op mobile
                        const el = window.document.getElementById(
                            "related-products-slider"
                        );

                        const mobileElement = el?.querySelector(
                            ".info-block__heading-button"
                        );

                        if (
                            mobileElement &&
                            mobileElement instanceof HTMLElement &&
                            !el?.classList.contains("info-block--active")
                        ) {
                            mobileElement.click();
                        }
                        scrollTo({
                            top: Math.max(
                                (el?.getBoundingClientRect().top ?? 0) -
                                    ScrollOffsets.DEFAULT,
                                0
                            ),
                            behavior: "smooth"
                        });
                    },
                    text: t("pdp.error.notSaleableSeparately.goTo")
                },
                activeScroll: true
            });
            return;
        }

        if (productDetail.deliveryType === DeliveryType.ClickAndCollect) {
            if (
                preferredStore &&
                !shouldDisableAddToCart(preferredStore.productAvailability)
            ) {
                onSubmit(
                    dispatch,
                    t,
                    cart,
                    productDetail,
                    cartProduct,
                    product,
                    productConfiguration,
                    configuration,
                    DeliveryType.ClickAndCollect,
                    preferredStore.code,
                    isAdditionalService,
                    openModal
                );
            } else {
                setStorePickerModalState(
                    () => StorePickerModalStates.AddToCart
                );
            }
        } else {
            onSubmit(
                dispatch,
                t,
                cart,
                productDetail,
                cartProduct,
                product,
                productConfiguration,
                configuration,
                DeliveryType.HomeDelivery,
                productDetail.storeCode,
                isAdditionalService,
                openModal
            );
        }
    };

export { onSubmitHandler };
